/**
 * Runner 3D, карта «Дата-центр»: коридор между серверными стойками.
 * Мир строится кодом, без `.glb`, но по тому же контракту, что улица:
 * `tile` длиной 60 м (центр в 0, повторяется) и `backdrop` (едет за камерой).
 *
 * Тайлы одинаковые и не двигаются — камера перескакивает на тайл назад,
 * поэтому всё в тайле повторяется каждые 60 м. Пасхалки (`decor`) живут
 * отдельно: считаются от пройденной дистанции и меняются по кругу, чтобы не
 * мелькать одним и тем же каждые 4 секунды.
 *
 * Стойки — один слитый меш корпусов и один слитый меш передних панелей:
 * индикаторы рисует шейдер (`ledMaterial`), мигание — по времени, разброс —
 * по локальной координате тайла (иначе при перескоке камеры узор прыгал бы).
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

export type DataCenterObstacleType = 'JUMP' | 'DUCK' | 'PUNCH' | 'DODGE';

export interface DataCenterWorld {
  readonly tile: THREE.Object3D;
  readonly backdrop: THREE.Object3D;
  readonly decor: THREE.Object3D;
  /** `distance` — пройдено с начала забега, м; `camZ` — как в сцене. */
  update(distance: number, camZ: number, now: number): void;
  reset(): void;
  makeObstacle(type: DataCenterObstacleType, width: number, shadow: (g: THREE.Group, rx: number, rz: number) => void): THREE.Group;
}

export const DATA_CENTER_BACKGROUND = 0x070d1a;

const TILE_LENGTH = 60;
const RACK_X = 9;
const RACK_DEPTH = 1.2;
const RACK_HEIGHT = 3.2;
const RACK_WIDTH = 0.8;
const RACKS_PER_GROUP = 11;
const GROUP_LENGTH = 10;
const TRAY_X = 6.8;
const TRAY_Y = 7.2;
const CEILING_Y = 10;
const FRONT_X = RACK_X - RACK_DEPTH / 2;
const DECOR_SLOTS = 6;
const DECOR_SPACING = 23;

// ─── Индикаторы ─────────────────────────────────────────────────────────
const ledVertex = /* glsl */ `
  #include <fog_pars_vertex>
  varying vec2 vUv;
  varying vec3 vLocal;
  void main() {
    vUv = uv;
    vLocal = position;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mvPosition;
    #include <fog_vertex>
  }
`;

const ledFragment = /* glsl */ `
  #include <fog_pars_fragment>
  uniform float uTime;
  uniform vec2 uSize;
  uniform float uRowHeight;
  varying vec2 vUv;
  varying vec3 vLocal;

  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }

  vec3 led(vec2 m, vec2 c, vec3 color, float seed, float speed) {
    float d = length(m - c);
    float on = step(0.35, fract(seed * 7.3 + uTime * speed));
    return color * on * (smoothstep(0.022, 0.012, d) * 2.2 + exp(-d * 55.0) * 0.35);
  }

  void main() {
    float rows = max(1.0, floor(uSize.y / uRowHeight + 0.5));
    float row = floor(vUv.y * rows);
    vec2 cell = vec2(vUv.x, fract(vUv.y * rows));
    // Метры внутри юнита: одинаковые точки на любой ширине панели.
    vec2 m = vec2(vUv.x * uSize.x, cell.y * uSize.y / rows);
    float rowH = uSize.y / rows;
    float seed = hash(vec2(row, floor(vLocal.z * 1.25) + floor(vLocal.x * 0.5) * 17.0));

    vec3 col = vec3(0.05, 0.06, 0.085);
    // Щель между юнитами.
    col = mix(col, vec3(0.01, 0.012, 0.02), step(cell.y, 0.07));
    // Корзины дисков.
    float bays = step(0.32, vUv.x) * step(vUv.x, 0.94) * step(0.55, fract(vUv.x * uSize.x * 16.0))
      * step(0.22, cell.y) * step(cell.y, 0.82);
    col += bays * 0.045;
    // Пустые юниты — глухая заглушка.
    if (seed > 0.86) col = vec3(0.025, 0.03, 0.04);

    float y = rowH * 0.5;
    if (seed <= 0.86) {
      col += led(m, vec2(0.06, y), vec3(0.15, 1.0, 0.45), seed, 0.6 + seed * 3.0);
      col += led(m, vec2(0.12, y), vec3(0.2, 0.6, 1.0), hash(vec2(seed, 1.0)), 1.5 + seed * 6.0);
      vec3 third = seed > 0.75 ? vec3(1.0, 0.55, 0.1) : vec3(0.15, 1.0, 0.45);
      col += led(m, vec2(0.18, y), third, hash(vec2(seed, 2.0)), 0.3 + seed);
    }
    gl_FragColor = vec4(col, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
    #include <fog_fragment>
  }
`;

const ledUniformsTime = { value: 0 };

/** Передняя панель сервера: юниты с мигающими индикаторами. `size` — ширина и высота панели, м. */
function ledMaterial(width: number, height: number, rowHeight = 0.26): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: THREE.UniformsUtils.merge([THREE.UniformsLib.fog, {
      uSize: { value: new THREE.Vector2(width, height) },
      uRowHeight: { value: rowHeight },
    }]) as Record<string, THREE.IUniform>,
    vertexShader: ledVertex,
    fragmentShader: ledFragment,
    fog: true,
  });
}

function withTime(m: THREE.ShaderMaterial): THREE.ShaderMaterial {
  // Общее время на все панели: один `update` двигает все индикаторы.
  m.uniforms.uTime = ledUniformsTime;
  return m;
}

// ─── Хелперы ────────────────────────────────────────────────────────────
const lambert = (color: number, emissive = 0x000000): THREE.MeshLambertMaterial =>
  new THREE.MeshLambertMaterial({ color, emissive });
const glow = (color: number): THREE.MeshBasicMaterial => new THREE.MeshBasicMaterial({ color });

function boxAt(w: number, h: number, d: number, x: number, y: number, z: number): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(w, h, d);
  g.translate(x, y, z);
  return g;
}

function merged(geos: THREE.BufferGeometry[], mat: THREE.Material): THREE.Mesh {
  const g = mergeGeometries(geos.map((x) => (x.index ? x.toNonIndexed() : x)), false);
  geos.forEach((x) => x.dispose());
  return new THREE.Mesh(g!, mat);
}

function part(parent: THREE.Object3D, geo: THREE.BufferGeometry, mat: THREE.Material,
  x: number, y: number, z: number, sx = 1, sy = 1, sz = 1): THREE.Mesh {
  const m = new THREE.Mesh(geo, mat);
  m.position.set(x, y, z);
  m.scale.set(sx, sy, sz);
  parent.add(m);
  return m;
}

/** Табличка с текстом на canvas. `lines` — строки сверху вниз. */
function label(lines: string[], opts: {
  w: number; h: number; bg: string; fg: string; font?: string; border?: string; align?: CanvasTextAlign;
}): THREE.Mesh {
  const px = 128;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(opts.w * px);
  canvas.height = Math.round(opts.h * px);
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = opts.bg;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  if (opts.border) {
    ctx.strokeStyle = opts.border;
    ctx.lineWidth = px * 0.06;
    ctx.strokeRect(px * 0.05, px * 0.05, canvas.width - px * 0.1, canvas.height - px * 0.1);
  }
  const size = Math.min(canvas.height / (lines.length + 0.6), (canvas.width * 1.6) / Math.max(...lines.map((l) => l.length)));
  ctx.font = opts.font ? opts.font.replace('{s}', `${Math.floor(size)}`) : `bold ${Math.floor(size)}px Menlo, Consolas, monospace`;
  ctx.fillStyle = opts.fg;
  ctx.textBaseline = 'middle';
  ctx.textAlign = opts.align ?? 'center';
  const x = ctx.textAlign === 'left' ? px * 0.2 : canvas.width / 2;
  lines.forEach((line, i) => ctx.fillText(line, x, (canvas.height / (lines.length + 1)) * (i + 1)));
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return new THREE.Mesh(new THREE.PlaneGeometry(opts.w, opts.h), new THREE.MeshBasicMaterial({ map: tex }));
}

/** Плитки фальшпола с перфорацией. */
function floorTexture(): THREE.CanvasTexture {
  const s = 128;
  const canvas = document.createElement('canvas');
  canvas.width = s;
  canvas.height = s;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#5d6778';
  ctx.fillRect(0, 0, s, s);
  ctx.fillStyle = '#4a5363';
  for (let y = 0; y < 8; y++) for (let x = 0; x < 8; x++) ctx.fillRect(22 + x * 11, 22 + y * 11, 5, 5);
  ctx.strokeStyle = '#2b313c';
  ctx.lineWidth = 4;
  ctx.strokeRect(0, 0, s, s);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 8;
  return tex;
}

// ─── Тайл ───────────────────────────────────────────────────────────────
function buildTile(): THREE.Group {
  const tile = new THREE.Group();
  tile.name = 'Tile';

  const floorTex = floorTexture();
  const floorWidth = RACK_X * 2 + 4;
  floorTex.repeat.set(floorWidth / 0.6, TILE_LENGTH / 0.6);
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(floorWidth, TILE_LENGTH), new THREE.MeshLambertMaterial({ map: floorTex }));
  floor.rotation.x = -Math.PI / 2;
  tile.add(floor);

  // Светящиеся «шины данных» между полосами и по краям дорожки.
  const strips: THREE.BufferGeometry[] = [];
  [-2.5, 2.5].forEach((x) => {
    for (let z = -TILE_LENGTH / 2; z < TILE_LENGTH / 2; z += 3) strips.push(boxAt(0.12, 0.02, 1.8, x, 0.01, z + 1.5));
  });
  [-7.6, 7.6].forEach((x) => strips.push(boxAt(0.18, 0.03, TILE_LENGTH, x, 0.015, 0)));
  tile.add(merged(strips, glow(0x22d3ee)));

  // Стойки: корпуса, рамы и передние панели с индикаторами.
  const bodies: THREE.BufferGeometry[] = [];
  const frames: THREE.BufferGeometry[] = [];
  const fronts: THREE.BufferGeometry[] = [];
  const caps: THREE.BufferGeometry[] = [];
  const groups = TILE_LENGTH / GROUP_LENGTH;
  for (const side of [-1, 1]) {
    for (let g = 0; g < groups; g++) {
      const start = -TILE_LENGTH / 2 + g * GROUP_LENGTH + 0.6;
      for (let r = 0; r < RACKS_PER_GROUP; r++) {
        const z = start + RACK_WIDTH * (r + 0.5);
        bodies.push(boxAt(RACK_DEPTH, RACK_HEIGHT, RACK_WIDTH - 0.04, side * RACK_X, RACK_HEIGHT / 2, z));
        frames.push(boxAt(0.06, RACK_HEIGHT, 0.05, side * (FRONT_X - 0.02), RACK_HEIGHT / 2, z - RACK_WIDTH / 2 + 0.03));
        const front = new THREE.PlaneGeometry(RACK_WIDTH - 0.14, RACK_HEIGHT - 0.3);
        front.rotateY(side < 0 ? Math.PI / 2 : -Math.PI / 2);
        front.translate(side * (FRONT_X - 0.005), RACK_HEIGHT / 2, z);
        fronts.push(front);
      }
      // Торец ряда — синяя панель с подсветкой, конец «холодного коридора».
      const end = start + RACK_WIDTH * RACKS_PER_GROUP;
      caps.push(boxAt(RACK_DEPTH + 0.1, RACK_HEIGHT + 0.2, 0.08, side * RACK_X, (RACK_HEIGHT + 0.2) / 2, end + 0.04));
    }
  }
  tile.add(merged(bodies, lambert(0x252b38)));
  tile.add(merged(frames, lambert(0x6b7385)));
  tile.add(merged(caps, lambert(0x1e3a8a, 0x0a1640)));
  tile.add(new THREE.Mesh(mergeGeometries(fronts.map((g) => g.toNonIndexed()))!,
    withTime(ledMaterial(RACK_WIDTH - 0.14, RACK_HEIGHT - 0.3))));

  // Кабельные лотки под потолком: лоток, подвесы, пучки кабелей.
  const trays: THREE.BufferGeometry[] = [];
  const cablesY: THREE.BufferGeometry[] = [];
  const cablesB: THREE.BufferGeometry[] = [];
  const lights: THREE.BufferGeometry[] = [];
  for (const side of [-1, 1]) {
    const x = side * TRAY_X;
    trays.push(boxAt(1.2, 0.06, TILE_LENGTH, x, TRAY_Y, 0));
    trays.push(boxAt(0.05, 0.25, TILE_LENGTH, x - 0.6, TRAY_Y + 0.12, 0));
    trays.push(boxAt(0.05, 0.25, TILE_LENGTH, x + 0.6, TRAY_Y + 0.12, 0));
    for (let z = -TILE_LENGTH / 2 + 1.5; z < TILE_LENGTH / 2; z += 6) {
      trays.push(boxAt(0.05, CEILING_Y - TRAY_Y, 0.05, x - 0.6, (CEILING_Y + TRAY_Y) / 2, z));
      trays.push(boxAt(0.05, CEILING_Y - TRAY_Y, 0.05, x + 0.6, (CEILING_Y + TRAY_Y) / 2, z));
    }
    cablesY.push(boxAt(0.45, 0.14, TILE_LENGTH, x - 0.25, TRAY_Y + 0.1, 0));
    cablesB.push(boxAt(0.4, 0.12, TILE_LENGTH, x + 0.25, TRAY_Y + 0.09, 0));
  }
  for (let z = -TILE_LENGTH / 2 + 2.5; z < TILE_LENGTH / 2; z += 5) lights.push(boxAt(1.4, 0.08, 2.2, 0, CEILING_Y - 0.05, z));
  tile.add(merged(trays, lambert(0x8a93a6)));
  tile.add(merged(cablesY, lambert(0xf2c230)));
  tile.add(merged(cablesB, lambert(0x2563eb)));
  tile.add(merged(lights, glow(0xdbeafe)));
  return tile;
}

function buildBackdrop(): THREE.Group {
  const g = new THREE.Group();
  g.name = 'Backdrop';
  // Без освещения: снизу на потолок свет не падает, Lambert дал бы чёрную дыру.
  const dark = new THREE.MeshBasicMaterial({ color: 0x141d33 });
  const ceiling = new THREE.Mesh(new THREE.PlaneGeometry(40, 800), dark);
  ceiling.rotation.x = Math.PI / 2;
  ceiling.position.set(0, CEILING_Y, -380);
  g.add(ceiling);
  [-1, 1].forEach((side) => {
    const wall = new THREE.Mesh(new THREE.PlaneGeometry(800, CEILING_Y), dark);
    wall.rotation.y = side < 0 ? Math.PI / 2 : -Math.PI / 2;
    wall.position.set(side * (RACK_X + 1.5), CEILING_Y / 2, -380);
    g.add(wall);
  });
  return g;
}

// ─── Пасхалки ───────────────────────────────────────────────────────────
// Каждая строится в своей системе: начало — на лицевой стороне стойки,
// +z — в коридор (так смотрят таблички), x — вдоль коридора. Ставим на сторону
// `side` (−1 слева, 1 справа) и поворачиваем лицом в коридор.
type DecorKind = { build: () => THREE.Group; tick?: (g: THREE.Group, now: number) => void };

function faceCorridor(m: THREE.Object3D, side: number): void {
  m.rotation.y = side < 0 ? Math.PI / 2 : -Math.PI / 2;
}

const decorKinds: DecorKind[] = [
  {
    // Табличка на стойке.
    build: () => {
      const g = new THREE.Group();
      const sign = label(['404', 'ROOM NOT FOUND'], { w: 2.4, h: 1.1, bg: '#b91c1c', fg: '#ffffff', border: '#ffffff' });
      sign.position.set(0, 2.4, 0);
      g.add(sign);
      return g;
    },
  },
  {
    build: () => {
      const g = new THREE.Group();
      const note = label(['DO NOT', 'TOUCH', 'PROD!'], { w: 1.3, h: 1.3, bg: '#fde047', fg: '#1f2937', font: 'bold {s}px "Marker Felt", "Comic Sans MS", sans-serif' });
      note.position.set(0, 1.7, 0);
      note.rotation.z = 0.08;
      g.add(note);
      return g;
    },
  },
  {
    // Огромные клавиши Ctrl+C / Ctrl+V у стойки.
    build: () => {
      const g = new THREE.Group();
      ([['Ctrl', -1.9], ['C', -0.95], ['Ctrl', 0.45], ['V', 1.4]] as const).forEach(([text, x]) => {
        part(g, new THREE.BoxGeometry(0.85, 0.45, 0.75), lambert(0xe5e7eb), x, 0.22, 0.5);
        const cap = label([text], { w: 0.9, h: 0.7, bg: '#f3f4f6', fg: '#111827' });
        cap.rotation.x = -Math.PI / 2;
        cap.scale.setScalar(0.8);
        cap.position.set(x, 0.451, 0.5);
        g.add(cap);
      });
      return g;
    },
  },
  {
    // Кофемашина «java».
    build: () => {
      const g = new THREE.Group();
      part(g, new THREE.BoxGeometry(1.0, 1.7, 0.8), lambert(0x374151), 0, 0.85, 0.4);
      part(g, new THREE.BoxGeometry(0.6, 0.5, 0.2), lambert(0x111827), 0, 1.0, 0.85);
      part(g, new THREE.CylinderGeometry(0.12, 0.1, 0.22, 12), lambert(0xffffff), 0, 0.62, 0.9);
      const sign = label(['java \u2615'], { w: 0.9, h: 0.35, bg: '#78350f', fg: '#fde68a' });
      sign.position.set(0, 1.45, 0.81);
      g.add(sign);
      return g;
    },
  },
  {
    // Терминал на тележке: курсор мигает.
    build: () => {
      const g = new THREE.Group();
      part(g, new THREE.BoxGeometry(0.7, 1.1, 0.7), lambert(0x4b5563), 0, 0.55, 0.45);
      part(g, new THREE.BoxGeometry(1.6, 1.0, 0.12), lambert(0x111827), 0, 1.65, 0.4);
      const screen = label(['$ git push --force', '> it works on', '  my machine'], {
        w: 1.5, h: 0.9, bg: '#020617', fg: '#4ade80', align: 'left',
      });
      screen.position.set(0, 1.65, 0.461);
      g.add(screen);
      const cursor = part(g, new THREE.PlaneGeometry(0.06, 0.12), glow(0x4ade80), 0.05, 1.42, 0.463);
      cursor.name = 'cursor';
      return g;
    },
    tick: (g, now) => { g.getObjectByName('cursor')!.visible = Math.floor(now / 500) % 2 === 0; },
  },
  {
    // Резиновая уточка на стойке — rubber duck debugging.
    build: () => {
      const g = new THREE.Group();
      const yellow = lambert(0xfacc15);
      const z = -RACK_DEPTH / 2;
      part(g, new THREE.SphereGeometry(0.55, 16, 12), yellow, 0, RACK_HEIGHT + 0.42, z, 1.25, 0.8, 1);
      part(g, new THREE.SphereGeometry(0.33, 16, 12), yellow, 0, RACK_HEIGHT + 0.95, z + 0.3);
      part(g, new THREE.ConeGeometry(0.12, 0.3, 10), lambert(0xf97316), 0, RACK_HEIGHT + 0.92, z + 0.68).rotation.x = Math.PI / 2;
      [-0.14, 0.14].forEach((dx) => part(g, new THREE.SphereGeometry(0.05, 8, 6), lambert(0x111111), dx, RACK_HEIGHT + 1.05, z + 0.58));
      return g;
    },
  },
  {
    build: () => {
      const g = new THREE.Group();
      const board = label(['UPTIME 99.98%', 'DAYS WITHOUT BUGS: 0'], { w: 3.2, h: 1.1, bg: '#030712', fg: '#22c55e', border: '#22c55e' });
      board.position.set(0, 4.6, 0);
      g.add(board);
      return g;
    },
  },
  {
    // Гигантская дискета «Сохранить», прислонена к стойке.
    build: () => {
      const g = new THREE.Group();
      const disk = new THREE.Group();
      part(disk, new THREE.BoxGeometry(1.4, 1.4, 0.08), lambert(0x1e3a8a), 0, 0, 0);
      part(disk, new THREE.BoxGeometry(0.7, 0.42, 0.1), lambert(0xc0c4cc), 0.05, 0.47, 0);
      part(disk, new THREE.BoxGeometry(1.0, 0.6, 0.1), lambert(0xf3f4f6), 0, -0.3, 0);
      disk.rotation.x = -0.25;
      disk.position.set(0, 0.72, 0.25);
      g.add(disk);
      return g;
    },
  },
  {
    build: () => {
      const g = new THREE.Group();
      const mat = label(["THERE'S NO PLACE", 'LIKE 127.0.0.1'], { w: 2.6, h: 0.8, bg: '#7c2d12', fg: '#fed7aa' });
      mat.rotation.x = -Math.PI / 2;
      mat.position.set(0, 0.02, 0.55);
      g.add(mat);
      return g;
    },
  },
  {
    build: () => {
      const g = new THREE.Group();
      const sign = label(['HAVE YOU TRIED', 'TURNING IT OFF', 'AND ON AGAIN?'], { w: 2.2, h: 1.3, bg: '#f8fafc', fg: '#0f172a', border: '#0ea5e9' });
      sign.position.set(0, 2.3, 0);
      g.add(sign);
      return g;
    },
  },
];

/** Пасхалки: слоты на фиксированной дистанции, каждый уходит за камеру и встаёт вперёд со следующей шуткой. */
function buildDecor(): Pick<DataCenterWorld, 'decor' | 'update' | 'reset'> {
  const decor = new THREE.Group();
  const kinds = decorKinds.map((k) => ({ ...k, group: k.build() }));
  kinds.forEach((k) => { k.group.visible = false; decor.add(k.group); });
  const slots: { at: number; kind: number; side: number }[] = [];
  let nextKind = 0;

  function place(slot: (typeof slots)[number]): void {
    const k = kinds[slot.kind].group;
    k.visible = true;
    k.position.x = slot.side * (FRONT_X - 0.02);
    faceCorridor(k, slot.side);
  }

  function reset(): void {
    kinds.forEach((k) => { k.group.visible = false; });
    slots.length = 0;
    nextKind = 0;
    for (let i = 0; i < DECOR_SLOTS; i++) {
      // Сторона и шутка разные у соседей; первая — в 30 м от старта.
      slots.push({ at: 30 + i * DECOR_SPACING, kind: nextKind, side: i % 2 ? 1 : -1 });
      nextKind = (nextKind + 1) % kinds.length;
    }
    slots.forEach(place);
  }

  function update(distance: number, camZ: number, now: number): void {
    ledUniformsTime.value = now / 1000;
    for (const slot of slots) {
      if (slot.at - distance < -15) {
        kinds[slot.kind].group.visible = false;
        slot.at += DECOR_SLOTS * DECOR_SPACING;
        slot.kind = nextKind;
        nextKind = (nextKind + 1) % kinds.length;
        // Шутка не должна оказаться в двух слотах сразу.
        while (slots.some((s) => s !== slot && s.kind === slot.kind)) {
          slot.kind = nextKind;
          nextKind = (nextKind + 1) % kinds.length;
        }
        place(slot);
      }
      const k = kinds[slot.kind];
      k.group.position.z = camZ - (slot.at - distance);
      k.tick?.(k.group, now);
    }
  }

  reset();
  return { decor, update, reset };
}

// ─── Препятствия ────────────────────────────────────────────────────────
// Габариты как у уличных: стенка ~1,2 м, перекладина 1,7–2,3 м, ширина
// полосы 4,4 м, на всю ширину — 14,6 м.
function buildObstacles(): DataCenterWorld['makeObstacle'] {
  const box = new THREE.BoxGeometry(1, 1, 1);
  const mats = {
    rack: lambert(0x252b38),
    steel: lambert(0x8a93a6),
    dark: lambert(0x111827),
    hazard: lambert(0xf2c230),
    black: lambert(0x1f2937),
    alert: glow(0xef4444),
    cable: [lambert(0xf2c230), lambert(0x2563eb), lambert(0xef4444), lambert(0x22c55e)],
    bug: lambert(0xdc2626),
    bugHead: lambert(0x18181b),
    eye: glow(0xa3e635),
    pixel: [glow(0xef4444), glow(0x22d3ee), glow(0xa3e635), lambert(0x18181b)],
    wheel: lambert(0x0f172a),
    cart: lambert(0x64748b),
  };

  /** Упавший сервер поперёк полосы — перепрыгнуть. */
  function fallenServer(width: number): THREE.Group {
    const g = new THREE.Group();
    const w = width - 0.2;
    part(g, box, mats.rack, 0, 0.55, 0, w, 1.1, 0.9);
    const panel = new THREE.Mesh(new THREE.PlaneGeometry(w - 0.1, 1.0), withTime(ledMaterial(w - 0.1, 1.0, 0.25)));
    panel.position.set(0, 0.55, 0.451);
    g.add(panel);
    for (let x = -w / 2 + 0.6; x < w / 2; x += 1.6) part(g, box, mats.alert, x, 1.14, 0.2, 0.18, 0.08, 0.18);
    shadowOf(g, width * 0.55, 0.6);
    return g;
  }

  /** Провисший кабельный лоток на стойках — пригнуться. */
  function saggingTray(width: number): THREE.Group {
    const g = new THREE.Group();
    [-1, 1].forEach((s) => part(g, box, mats.steel, s * (width / 2 - 0.15), 1.25, 0, 0.2, 2.5, 0.2));
    part(g, box, mats.steel, 0, 2.28, 0, width, 0.08, 0.6);
    [-1, 1].forEach((s) => part(g, box, mats.steel, 0, 2.38, s * 0.3, width, 0.2, 0.04));
    mats.cable.forEach((mat, i) => {
      const z = -0.2 + i * 0.13;
      const sag = 0.35 + i * 0.07;
      const curve = new THREE.CatmullRomCurve3([
        new THREE.Vector3(-width / 2 + 0.3, 2.28, z),
        new THREE.Vector3(-width / 4, 2.28 - sag * 0.75, z),
        new THREE.Vector3(0, 2.28 - sag, z),
        new THREE.Vector3(width / 4, 2.28 - sag * 0.75, z),
        new THREE.Vector3(width / 2 - 0.3, 2.28, z),
      ]);
      g.add(new THREE.Mesh(new THREE.TubeGeometry(curve, 24, 0.055, 6), mat));
    });
    // Жёлто-чёрная бирка «низкий кабель».
    const tag = new THREE.Group();
    const stripes = 6;
    for (let i = 0; i < stripes; i++) part(tag, box, i % 2 ? mats.black : mats.hazard, -0.45 + i * 0.18, 0, 0, 0.18, 0.3, 0.04);
    tag.position.set(0, 1.72, 0.32);
    g.add(tag);
    shadowOf(g, width * 0.5, 0.4);
    return g;
  }

  /** Баг-жук — разбить ударом, рассыпается пикселями. */
  function bugs(width: number): THREE.Group {
    const g = new THREE.Group();
    const count = Math.max(1, Math.round(width / 4.4));
    for (let i = 0; i < count; i++) {
      const x = -width / 2 + (width / count) * (i + 0.5);
      const bug = new THREE.Group();
      part(bug, new THREE.SphereGeometry(1, 18, 12), mats.bug, 0, 0.95, 0, 1.0, 0.75, 1.2);
      part(bug, box, mats.bugHead, 0, 1.15, 0, 0.06, 0.08, 2.2);
      part(bug, new THREE.SphereGeometry(0.5, 14, 10), mats.bugHead, 0, 1.05, 1.15);
      [-0.22, 0.22].forEach((dx) => {
        part(bug, new THREE.SphereGeometry(0.11, 10, 8), mats.eye, dx, 1.2, 1.55);
        const antenna = part(bug, new THREE.CylinderGeometry(0.03, 0.03, 0.9, 6), mats.bugHead, dx * 1.6, 1.75, 1.35);
        antenna.rotation.set(0.6, 0, dx * 1.8);
      });
      [-1, 1].forEach((s) => [-0.5, 0, 0.5].forEach((z) => {
        const leg = part(bug, new THREE.CylinderGeometry(0.05, 0.05, 1.0, 6), mats.bugHead, s * 1.05, 0.5, z);
        leg.rotation.z = s * 0.9;
      }));
      // Пятна-«точки».
      [[-0.4, 0.3], [0.45, -0.1], [-0.3, -0.5], [0.35, 0.55]].forEach(([dx, dz]) =>
        part(bug, new THREE.SphereGeometry(0.2, 10, 8), mats.bugHead, dx, 1.6, dz, 1, 0.4, 1));
      bug.position.x = x;
      g.add(bug);
      for (let p = 0; p < 18; p++) {
        const px = part(g, box, mats.pixel[p % mats.pixel.length], 0, 0, 0, 0.32, 0.32, 0.32);
        px.visible = false;
        px.userData.home = new THREE.Vector3(x + (Math.random() - 0.5) * 2, 0.4 + Math.random() * 1.4, (Math.random() - 0.5) * 2);
        px.userData.velocity = new THREE.Vector3((Math.random() - 0.5) * 8, 3 + Math.random() * 4, -4 - Math.random() * 6);
        px.userData.spin = new THREE.Vector3(Math.random() * 8, Math.random() * 8, 0);
      }
    }
    g.userData.crates = true;
    shadowOf(g, width * 0.5, 0.9);
    return g;
  }

  /** Тележка с серверами в полосе — обойти в соседнюю. */
  function serverCarts(width: number): THREE.Group {
    const g = new THREE.Group();
    const count = Math.max(1, Math.round(width / 5));
    for (let i = 0; i < count; i++) {
      const x = -width / 2 + (width / count) * (i + 0.5);
      const cart = new THREE.Group();
      part(cart, box, mats.cart, 0, 0.45, 0, 2.4, 0.12, 1.5);
      [-1, 1].forEach((sx) => [-1, 1].forEach((sz) =>
        part(cart, new THREE.CylinderGeometry(0.16, 0.16, 0.12, 12), mats.wheel, sx * 1.0, 0.2, sz * 0.55).rotation.z = Math.PI / 2));
      [-1, 1].forEach((sx) => part(cart, box, mats.steel, sx * 1.15, 1.2, 0.68, 0.06, 1.5, 0.06));
      part(cart, box, mats.steel, 0, 1.95, 0.68, 2.36, 0.06, 0.06);
      for (let s = 0; s < 3; s++) {
        const y = 0.72 + s * 0.44;
        const sw = 2.0 - s * 0.15;
        part(cart, box, mats.rack, 0, y, -0.05, sw, 0.4, 1.2);
        const panel = new THREE.Mesh(new THREE.PlaneGeometry(sw - 0.06, 0.36), withTime(ledMaterial(sw - 0.06, 0.36, 0.36)));
        panel.position.set(0, y, 0.551);
        cart.add(panel);
      }
      cart.position.x = x;
      g.add(cart);
    }
    shadowOf(g, width * 0.45, 0.9);
    return g;
  }

  let shadowOf: (g: THREE.Group, rx: number, rz: number) => void = () => {};
  return (type, width, shadow) => {
    shadowOf = shadow;
    switch (type) {
      case 'JUMP': return fallenServer(width);
      case 'DUCK': return saggingTray(width);
      case 'PUNCH': return bugs(width);
      default: return serverCarts(width);
    }
  };
}

export function createDataCenterWorld(): DataCenterWorld {
  const { decor, update, reset } = buildDecor();
  return {
    tile: buildTile(),
    backdrop: buildBackdrop(),
    decor,
    update,
    reset,
    makeObstacle: buildObstacles(),
  };
}
